import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import * as ROUTES from '../../../constants/routes';

const KOALENDAR = 'https://koalendar.com/e/meet-with-mark-bernstein';

// -- feature icons --
const S = (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{p}</svg>;
const IcUpload = () => S(<><polyline points="16 16 12 12 8 16" /><line x1="12" y1="12" x2="12" y2="21" /><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" /></>);
const IcServer = () => S(<><rect x="2" y="2" width="20" height="8" rx="2" /><rect x="2" y="14" width="20" height="8" rx="2" /><line x1="6" y1="6" x2="6.01" y2="6" /><line x1="6" y1="18" x2="6.01" y2="18" /></>);
const IcRefresh = () => S(<><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 .49-3.51" /></>);
const IcShield = () => S(<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />);
const IcAward = () => S(<><circle cx="12" cy="8" r="6" /><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11" /></>);
const IcUsers = () => S(<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>);
const IcBriefcase = () => S(<><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></>);
const IcZap = () => S(<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />);
const IcDatabase = () => S(<><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></>);
const IcMonitor = () => S(<><rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></>);
const IcFileCheck = () => S(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><path d="M9 15l2 2 4-4" /></>);
const IcCode = () => S(<><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></>);
const IcTrophy = () => S(<><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" /><line x1="8" y1="2" x2="8" y2="18" /><line x1="16" y1="6" x2="16" y2="22" /></>);
const IcLink = () => S(<><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></>);
const IcBars = () => S(<><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></>);
const IcCheck = () => S(<><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>);

const TABS = [
    {
        label: 'Sellers',
        h: 'Build once. Use everywhere.', hPink: 'Every window easier than the last.',
        lead: 'One upload populates all 17 disclosure sections. AI pre-fills 60% from your existing documents. Your team reviews, certifies, and submits — in days, not months.',
        features: [
            { Icon: IcUpload, t: 'One upload, used everywhere', d: 'PISCES data immediately feeds investment readiness and M&A preparation. No duplication.' },
            { Icon: IcServer, t: 'AI self-diligence sandbox', d: "Simulate what a buyer's AI will find — and fix gaps before advisors review." },
            { Icon: IcRefresh, t: 'Window-over-window economics', d: 'Second window: deltas only. Marginal cost per window falls with every repeat.' },
        ],
        cta: { text: 'Start your free eligibility check', to: ROUTES.REGISTER },
        card: { title: 'Disclosure module at a glance', type: 'stats', rows: [{ big: '60%', label: 'AI autofill from your documents' }, { big: '73', label: 'structured questions, all 17 CDs' }, { big: 'Δ', label: 're-certify changes only' }, { big: '1×', label: 'upload, used everywhere' }], note: '£4,999/year — replaces material professional fees for document collation' },
    },
    {
        label: 'Buyers',
        h: 'Better information.', hPink: 'More confident investment.',
        lead: 'All 17 Core Disclosures structured, AI-reviewed, and director-certified before the window opens. Every buyer sees the same information simultaneously — no asymmetry.',
        features: [
            { Icon: IcShield, t: 'AI-ready data', d: 'Seller data is structured so your own AI tools can interrogate it cleanly — no reformatting needed.' },
            { Icon: IcAward, t: 'Gold quality gate', d: 'Companies must reach Gold certification before investor matching. Only prepared companies reach your desk.' },
            { Icon: IcUsers, t: 'Full information parity', d: 'The same structured data available to all buyers simultaneously. No advantage to connected parties.' },
        ],
        card: { title: 'What buyers get', type: 'stats', rows: [{ big: '17', label: 'CDs structured & AI-reviewed per window' }, { big: 'AI', label: 'natural language query of seller data' }, { big: '40+', label: 'VCs on InvestorMatch' }, { big: '100%', label: 'information parity across all buyers' }] },
    },
    {
        label: 'Advisors',
        h: 'Focus on advice.', hPink: 'Not the document chase.',
        lead: 'AI pre-populates 60% of disclosures from existing documents before your first meeting. Advisors review, not build. Free access to all client data on the platform.',
        features: [
            { Icon: IcBriefcase, t: 'Included, not replaced', d: 'Advisors access all client data in one place at no charge. Add colleagues in one click.' },
            { Icon: IcZap, t: 'Collation eliminated', d: '60% of disclosures AI-pre-populated. Structured knowledge base queryable in natural language.' },
            { Icon: IcDatabase, t: 'One dataset, all parties', d: 'Company builds it once. Each authorised advisor accesses their relevant portion directly.' },
        ],
        cta: { text: 'Set up your free advisor account', to: ROUTES.REGISTER },
        card: { title: 'What advisors get', type: 'checks', rows: ['Free advisor access', '160+ structured subsections', 'Natural language query', '60+ advisors already on platform'] },
    },
    {
        label: 'Operators',
        h: 'Automated oversight.', hPink: 'Stronger regulatory standing.',
        lead: "The FCA requires operators to satisfy themselves all 17 CDs are complete before every trading window. Most manage this with a Word document. Diligentsia is the infrastructure layer that makes this scalable.",
        features: [
            { Icon: IcMonitor, t: 'Live risk dashboard', d: "Every applicant's completion, risk score, and compliance gate in one view. Flags before the window opens." },
            { Icon: IcFileCheck, t: 'Automated regulatory file', d: "AI produces a scored review record per section — your structured FCA 'reasonable review' document, automatically." },
            { Icon: IcCode, t: 'API + MCP integration', d: 'REST API and MCP support — surface disclosure workflow outputs inside your existing admission portal.' },
        ],
        cta: { text: 'Discuss an operator partnership', href: KOALENDAR },
        card: { title: 'Core Disclosures', type: 'checks', rows: ['17-CD framework, 73 questions', 'Traffic light quality scoring', 'Window-over-window delta tracking', 'Hard gate enforcement (CD7/CD12)', 'Immutable audit log', 'FCA compliance pack generator'] },
    },
    {
        label: 'Regulators',
        h: 'The oversight infrastructure', hPink: 'PS 25/6 describes — already built.',
        quote: { text: 'Such checks could be automated, such as checking that companies have provided information required by the core information disclosures.', src: '— FCA PS 25/6 para 2.50' },
        features: [
            { Icon: IcTrophy, t: 'Uniform structured reporting', d: 'Consistent disclosure data across all operators — cross-operator benchmarking and supervisory review made simple.' },
            { Icon: IcLink, t: 'Chain-of-evidence audit trail', d: 'Every action, data change, and director certification in an immutable, timestamped, queryable log.' },
            { Icon: IcBars, t: 'Proportionate oversight by design', d: 'Automated flags mean a 3-person team can oversee 50 companies — the proportionate model PS 25/6 describes.' },
        ],
        cta: { text: 'Request a regulator briefing', href: KOALENDAR },
        card: { title: 'Let’s discuss', type: 'numbered', rows: [{ no: '01', t: 'Core Disclosure oversight', sub: 'PSM Rule 3.4' }, { no: '02', t: 'Timing compliance', sub: 'PSM Rule 3.6, 3.8' }, { no: '03', t: 'Record keeping, 5 years', sub: 'MAR Art. 18' }] },
    },
];

const CtaButton = ({ cta }) => {
    if (!cta) return null;
    if (cta.href) return <a className="lp-btn lp-btn--primary" href={cta.href} target="_blank" rel="noopener noreferrer" style={{ borderRadius: '50px' }}>{cta.text}</a>;
    return <Link className="lp-btn lp-btn--primary" to={cta.to} style={{ borderRadius: '50px' }}>{cta.text}</Link>;
};

const SideCard = ({ card }) => (
    <div className="lp-aud-card">
        <h3>{card.title}</h3>
        {card.type === 'stats' && (
            <div className="lp-aud-statlist">
                {card.rows.map((r, i) => <div className="row" key={i}><span className="big">{r.big}</span><span className="lbl">{r.label}</span></div>)}
            </div>
        )}
        {card.type === 'checks' && (
            <div className="lp-aud-checklist">
                {card.rows.map((r, i) => <div className="row" key={i}><i className="ic"><IcCheck /></i><span>{r}</span></div>)}
            </div>
        )}
        {card.type === 'numbered' && (
            <div className="lp-aud-numlist">
                {card.rows.map((r, i) => <div className="row" key={i}><span className="no">{r.no}</span><span className="txt"><p style={{ fontSize: '14px', fontWeight: '500' }}>{r.t}</p><span className='w100'>{r.sub}</span></span></div>)}
            </div>
        )}
        {card.note && <div className="lp-aud-note">{card.note}</div>}
    </div>
);

const PiscesAudienceTabs = () => {
    const [active, setActive] = useState(0);
    const [progress, setProgress] = useState(0); // 0..1 through the scroll wrapper
    const wrapRef = useRef(null);

    // Reason: derive the active audience from how far the sticky wrapper has been scrolled (matches Companies lifecycle scroll-scrub)
    const compute = useCallback(() => {
        const wrap = wrapRef.current;
        if (!wrap) return;
        const wTop = wrap.getBoundingClientRect().top + window.scrollY;
        const range = wrap.offsetHeight - window.innerHeight;
        if (range <= 0) { setProgress(0); return; }
        const scrolled = Math.max(0, Math.min(window.scrollY - wTop, range));
        const p = scrolled / range;
        setProgress(p);
        const idx = Math.max(0, Math.min(TABS.length - 1, Math.floor(p * TABS.length)));
        setActive((prev) => (prev === idx ? prev : idx));
    }, []);

    useEffect(() => {
        compute();
        window.addEventListener('scroll', compute, { passive: true });
        window.addEventListener('resize', compute, { passive: true });
        return () => {
            window.removeEventListener('scroll', compute);
            window.removeEventListener('resize', compute);
        };
    }, [compute]);

    const goTo = (idx) => {
        const wrap = wrapRef.current;
        if (!wrap) return;
        const wTop = wrap.getBoundingClientRect().top + window.scrollY;
        const range = wrap.offsetHeight - window.innerHeight;
        if (range <= 0) { setActive(idx); return; } // mobile / un-pinned: switch on click
        const target = idx / (TABS.length - 1);
        window.scrollTo({ top: wTop + target * range, behavior: 'smooth' });
    };

    const tab = TABS[active];
    const barWidth = Math.max(4, Math.round(progress * 100));

    return (
        <div id="audiences" className="lp-aud-scroll" ref={wrapRef}>
            <section className="lp-aud-sticky">
                <div className="lp-aud-bar">
                    <div className="lp-aud-pills" role="tablist">
                        {TABS.map((t, i) => (
                            <button key={i} role="tab" className={`lp-aud-pill ${i === active ? 'is-active' : ''}`} onClick={() => goTo(i)}>{t.label}</button>
                        ))}
                    </div>
                    <div className="lp-aud-progress"><span style={{ width: `${barWidth}%` }} /></div>
                </div>

                <div className="lp-aud-body">
                    <div className="lp-aud-panel" key={active}>
                        <div className="lp-aud-copy">
                            <h2 className="lp-h2" style={{ fontSize: '30px' }}>{tab.h}<br /><span style={{ fontWeight: '600', color: '#FC7785' }}>{tab.hPink}</span></h2>
                            {tab.quote
                                ? <blockquote className="lp-aud-quote">“{tab.quote.text}”<footer>{tab.quote.src}</footer></blockquote>
                                : <p className="lp-aud-lead">{tab.lead}</p>}
                            <div className="lp-aud-features">
                                {tab.features.map(({ Icon, t, d }, i) => (
                                    <div className="lp-aud-feat" key={i}>
                                        <div className="ic"><Icon /></div>
                                        <div>
                                            <p style={{ fontSize: '14px', fontWeight: '500' }}>{t}</p>
                                            <span style={{ fontSize: '12px' }}>{d}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <CtaButton cta={tab.cta} />
                        </div>
                        <SideCard card={tab.card} />
                    </div>
                </div>
            </section>
        </div>
    );
};

export default PiscesAudienceTabs;
